/**
 * Entrada al ERP. Mismo diseño que la de NARA CRM (`paginas/Entrar.jsx`).
 * Se entra solo por el hub de aplicaciones (DA-9, apps.nara.chat; desde el
 * 2026-10-30 es la única forma de entrar): una sola vez para todas. Aquí no
 * hay pantalla de entrada ni Google directo: sin sesión, se va directo al
 * hub. La identidad la da Google (Firebase) en el hub; el acceso lo da la
 * tabla `users` de la base del cliente, así que un correo sin registro no
 * entra y el mensaje lo dice.
 */
import React, { useState, useEffect } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Typography } from "@mui/material";
import { Logout as LogoutIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { irAlHub, salirDelHub } from "../hub";
import { empresaActual } from "../empresa";

// DA-16: la empresa escogida, si hay; con varias y ninguna, el servidor
// responde 409 y la pantalla principal la pregunta.
const conEmpresa = (h = {}) => (empresaActual() ? { ...h, "X-Empresa": empresaActual() } : h);

const API_URL = process.env.REACT_APP_API_URL || "http://24.199.87.96:8001";

export default function Login() {
  const navigate = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && localStorage.getItem("user_id")) {
        navigate("/");
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Si ya hay sesión del hub (la cookie llega sola), se entra directo. Con
  // fetch y no con `api`: el 401 de `api` recarga /login y daría un bucle.
  useEffect(() => {
    fetch(`${API_URL}/auth/me`, { credentials: "include", headers: conEmpresa() })
      .then(async (r) => {
        if (r.status === 409) { navigate("/"); return null; }
        // 403: entró en el hub, pero su correo no tiene acceso al ERP.
        if (r.status === 403) {
          setError((await r.json()).detail || "Tu correo no tiene acceso a NARA ERP.");
          return null;
        }
        // 2026-10-30 (Raúl): se entra solo por el hub. Sin sesión, directo al
        // hub (ya no hay pantalla de entrada ni Google directo).
        if (!r.ok) { irAlHub(); return null; }
        return r.json();
      })
      .then((data) => {
        if (!data) return;
        localStorage.setItem("user_id", data.user_id);
        localStorage.setItem("user_email", data.email);
        localStorage.setItem("user_nombre", data.nombre);
        localStorage.setItem("user_rol", data.rol);
        navigate("/");
      })
      .catch(() => irAlHub());
  }, [navigate]);

  return (
    <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: "#0f172a", p: 2 }}>
      <Paper sx={{ p: 5, width: "100%", maxWidth: 420, textAlign: "center" }}>
        <Typography sx={{ fontSize: 30, fontWeight: 800, color: "#1e3a5f", letterSpacing: -0.5 }}>
          NARA ERP
        </Typography>
        <Typography sx={{ color: "text.secondary", mb: 4, fontSize: 14 }}>
          Facturación, cartera, egresos y contabilidad.
        </Typography>
        {error ? (
          <>
            <Alert severity="warning" sx={{ mb: 2, textAlign: "left" }}>{error}</Alert>
            <Button fullWidth size="large" variant="contained" startIcon={<LogoutIcon />}
                    onClick={async () => { await auth.signOut(); localStorage.clear(); salirDelHub(); }}>
              Salir y probar con otra cuenta
            </Button>
          </>
        ) : (
          <>
            <Typography sx={{ mb: 2, fontSize: 15, fontWeight: 600 }}>Te llevamos a NARA Apps…</Typography>
            <Button fullWidth size="large" variant="contained" onClick={irAlHub}
                    startIcon={<CircularProgress size={18} color="inherit" />}>
              Ir a NARA Apps
            </Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
